
import React, { useState, useEffect } from "react";
import { Routes, Route, useParams, useLocation, useNavigate } from "react-router-dom";
import "./TenantRoutes.css";
import Sidebar from "../components/Sidebar";
import MemberList from "../components/member/MemberList";
import MemberAdd from "../components/member/MemberAdd";
import Dashboard from "../components/Dashboard";
import MemberDetail from "../components/member/MemberDetail";
import EventList from "../components/event/EventList";
import EventAdd from "../components/event/EventAdd";
import Event from "../components/event/Event";
import EventDetail from "../components/event/EventDetail";
import EventItemAdd from "../components/event/EventItemAdd";
import EventItemDetail from "../components/event/EventItemDetail";
import AppHeader from "../components/AppHeader";
import TaskAdd from "../components/event/TaskAdd";
import EventHub from "../components/event/EventHub";
import EventAction from "../components/event/EventAction";
import UserAdd from "../components/tenant/UserAdd";
import Integrations from "../components/Integrations";
import AppFooter from "../components/AppFooter";
import MediaPost from "../components/media/MediaPost";
import TenantSettings from './TenantSettings';
import CalendarPage from "../pages/CalendarPage";
import PasswordResetForm from "../components/PasswordResetForm";
import ResetPassword from "../components/ResetPassword";
import { MessagesProvider, useMessagesUI } from "../context/MessagesContext";
import MessageThreadModal from "./messages/MessageThreadModal";
import EventHistory from "./history/EventHistory";
import PublicEventHistory from "./history/PublicEventHistory";
import MemberLogin from "./member/MemberLogin";
import TenantUsers from "./tenant/TenantUsers";
import UserDetail from "./tenant/UserDetail";
import SchedulePost from "./media/SchedulePost";
import Raffle from "../components/event/Raffle";
import CollaborationInvitation from "../components/tenant/CollaborationInvitation";
import AuctionRaffleSetup from "./event/AuctionRaffleSetup";
import AuctionConfig from "./event/AuctionConfig";
import RaffleConfig from "./event/RaffleConfig";
import YouTubeIntegration from './YouTubeIntegration';
import YouTubeAnalytics from './YouTubeAnalytics';
import SocialAnalytics from './analytics/SocialAnalytics';
import PostAnalytics from './analytics/PostAnalytics';
import MediaPostEnhanced from './media/MediaPostEnhanced'
import ScheduledPostsList from "./media/ScheduledPostsList";
import ScheduledEventsList from "./event/ScheduledEventsList";
import BillingDashboard from "./payment/BillingDashboard";
import SubscriptionPlans from "./payment/SubscriptionPlans";
import PaymentMethodForm from "./payment/PaymentMethodForm";
import PostsList from "./media/PostsList";
import NewPost from "./media/NewPost";
import SuperAdminDashboard from "./admin/SuperAdminDashboard";
import { FaArrowLeft, FaHome } from "react-icons/fa";

// Inline wrapper components for auction and raffle config
const AuctionConfigRoute = () => {
  const { tenant, eventId, itemId } = useParams();
  const navigate = useNavigate();
  
  const handleClose = () => {
    navigate(`/${tenant}/${eventId}/${itemId}/eventItemDetail`);
  };
  
  return (
    <div style={{ position: 'relative', width: '100%', minHeight: '100vh', padding: '20px', paddingBottom: 72, backgroundColor: '#f5f5f5' }}>
      <AuctionConfig
        tenantId={Number(tenant)}
        eventId={Number(eventId)}
        itemId={Number(itemId)}
        itemName=""
        onClose={handleClose}
      />
      <AppFooter
        buttons={[
          {
            icon: <FaArrowLeft />,
            label: "Back",
            onClick: () => navigate(`/${tenant}/${eventId}/${itemId}/eventItemDetail`),
          },
          {
            icon: <FaHome />,
            label: "Home",
            onClick: () => navigate(`/${tenant}/dashboard`),
          },
        ]}
      />
    </div>
  );
};

const RaffleConfigRoute = () => {
  const { tenant, eventId, itemId } = useParams();
  const navigate = useNavigate();
  
  const handleClose = () => {
    navigate(`/${tenant}/${eventId}/${itemId}/eventItemDetail`);
  };
  
  return (
    <div style={{ position: 'relative', width: '100%', minHeight: '100vh', padding: '20px', paddingBottom: 72, backgroundColor: '#f5f5f5' }}>
      <RaffleConfig
        tenantId={Number(tenant)}
        eventId={Number(eventId)}
        itemId={Number(itemId)}
        itemName=""
        onClose={handleClose}
      />
      <AppFooter
        buttons={[
          {
            icon: <FaArrowLeft />,
            label: "Back",
            onClick: () => navigate(`/${tenant}/${eventId}/${itemId}/eventItemDetail`),
          },
          {
            icon: <FaHome />,
            label: "Home",
            onClick: () => navigate(`/${tenant}/dashboard`),
          },
        ]}
      />
    </div>
  );
};

// Opens a message thread when ?openThread=<id> is in the URL (e.g. from push notification clicks)
const OpenThreadFromQuery = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { openMessages } = useMessagesUI();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const threadId = params.get('openThread');
    if (threadId) {
      openMessages(Number(threadId));
      // Remove the query param so it doesn't re-trigger on navigation
      params.delete('openThread');
      const newSearch = params.toString();
      navigate(
        { pathname: location.pathname, search: newSearch ? `?${newSearch}` : '' },
        { replace: true }
      );
    }
  }, [location.search, openMessages, navigate, location.pathname]);

  return null;
};

const TenantRoutes = () => {
  const { tenant } = useParams();
  const location = useLocation();
  
  // Hide header and sidebar for all EventAction route variations and PublicEventHistory
  const hideHeaderAndSidebar = location.pathname.toLowerCase().includes("/eventaction") ||
                                location.pathname.toLowerCase().includes("/publiceventhistory") ||
                                location.pathname.toLowerCase().includes("/memberlogin") ||
                                location.pathname.toLowerCase().match(/\/event\/[^/]+\/item\/[^/]+\/action$/) ||
                                location.pathname.toLowerCase().match(/\/\d+\/\d+\/action$/);

  // Detect public member pages (MemberAdd, MemberDetail) - show modified header, no sidebar
  const isMemberPublicPage = location.pathname.toLowerCase().endsWith("/memberadd") ||
                              location.pathname.toLowerCase().endsWith("/memberdetail");

  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const toggleSidebar = () => setIsSidebarVisible(v => !v);

  // Track desktop breakpoint for always-visible sidebar
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 1024px)');
    const handler = (e) => setIsDesktop(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    setIsSidebarVisible(false);
  }, [location.pathname, location.search, location.hash]);

  return (
    <MessagesProvider>
      <OpenThreadFromQuery />
      <div className={hideHeaderAndSidebar ? "public-page" : "tenant-page"}>
        {!hideHeaderAndSidebar && (
          <div>
            <AppHeader
              isSidebarVisible={isSidebarVisible}
              toggleSidebar={toggleSidebar}
              hideMessagesBell={isMemberPublicPage}
              disableSidebarToggle={isMemberPublicPage}
            />
            {!isMemberPublicPage && (isSidebarVisible || isDesktop) && <Sidebar tenantId={tenant} />}
          </div>
        )}
        <div className="container">
          <div className="tenant-content">
            <Routes>
              <Route path="reset-password" element={<PasswordResetForm tenantId={tenant} />} />
              <Route path="reset-password/:token" element={<ResetPassword tenantId={tenant} />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />

              <Route path="integrations" element={<Integrations tenantId={tenant} />} />
              <Route path="members" element={<MemberList tenantId={tenant} />} />
              <Route path="memberAdd" element={<MemberAdd tenantId={tenant} />} />
              <Route path="memberDetail" element={<MemberDetail tenantId={tenant} />} />
              <Route path="userAdd" element={<UserAdd tenantId={tenant} />} />

              <Route path="events" element={<EventList tenantId={tenant} />} />
              <Route path="eventAdd" element={<EventAdd tenantId={tenant} />} />
              <Route path=":eventId/event" element={<Event tenantId={tenant} />} />
              <Route path=":eventId/eventDetail" element={<EventDetail tenantId={tenant} />} />
              <Route path=":eventId/eventItemAdd" element={<EventItemAdd tenantId={tenant} />} />
              <Route path=":eventId/:itemId/eventItemDetail" element={<EventItemDetail tenantId={tenant} />} />
              <Route path="events/scheduled-events" element={<ScheduledEventsList tenantId={tenant} />} />
              <Route path=":eventId/task" element={<TaskAdd tenantId={tenant} />} />
              <Route path="eventHub" element={<EventHub tenantId={tenant} />} />

              {/* EventAction routes - all variations will hide AppHeader */}
              <Route path="event/:eventId/item/:itemId/action" element={<EventAction tenantId={tenant} />} />
              <Route path=":eventId/:itemId/action" element={<EventAction tenantId={tenant} />} />
              <Route path=":eventId/:itemId/eventAction" element={<EventAction tenantId={tenant} />} />
              <Route path=":eventId/:itemId/eventaction" element={<EventAction tenantId={tenant} />} />

              <Route path="dashboard" element={<Dashboard tenantId={tenant} />} />
              <Route path="calendar" element={<CalendarPage tenantId={tenant} />} />

              <Route path=":eventId/mediaPost" element={<MediaPost tenantId={tenant} />} />
              <Route path=":eventId/:itemId/mediaPost" element={<MediaPost tenantId={tenant} />} />
              <Route path=":eventId/:itemId/mediaPostEnhanced" element={<MediaPostEnhanced tenantId={tenant} />} />

              <Route path="settings" element={<TenantSettings tenantId={tenant} />} />
              <Route path="eventHistory" element={<EventHistory tenantId={tenant} />} />
              <Route path="publicEventHistory" element={<PublicEventHistory tenantId={tenant} />} />
              <Route path="memberLogin" element={<MemberLogin tenantId={tenant} />} />
              <Route path="tenantUsers" element={<TenantUsers tenantId={tenant} />} />
              <Route path=":userId/user" element={<UserDetail tenantId={tenant} />} />

              <Route path=":eventId/schedulePost" element={<SchedulePost tenantId={tenant} />} />
              <Route path=":eventId/:itemId/schedulePost" element={<SchedulePost tenantId={tenant} />} />
              <Route path="media/scheduled-posts" element={<ScheduledPostsList tenantId={tenant} />} />
              <Route path="media/schedule" element={<SchedulePost tenantId={tenant} />} />

              <Route path=":eventId/:itemId/raffle" element={<Raffle tenantId={tenant} />} />

              <Route path="collaborate" element={<CollaborationInvitation tenantId={tenant} />} />
              <Route path="youtube" element={<YouTubeIntegration tenantId={tenant} />} />
              <Route path="youtube-analytics" element={<YouTubeAnalytics tenantId={tenant} />} />

              {/* Social Analytics */}
              <Route path="analytics" element={<SocialAnalytics tenantId={tenant} />} />
              <Route path="analytics/posts" element={<PostAnalytics tenantId={tenant} />} />

              <Route path=":eventId/:itemId/actionSetup" element={<AuctionRaffleSetup tenantId={tenant} />} />
              <Route path=":eventId/:itemId/auctionConfig" element={<AuctionConfigRoute />} />
              <Route path=":eventId/:itemId/raffleConfig" element={<RaffleConfigRoute />} />

              {/* Billing routes */}
              <Route path="billing" element={<BillingDashboard tenantId={tenant} />} />
              <Route path="billing/subscription" element={<SubscriptionPlans tenantId={tenant} />} />
              <Route path="billing/payment-methods" element={<PaymentMethodForm tenantId={tenant} />} />

              <Route path="post" element={<PostsList tenantId={tenant} />} />
              <Route path="post/new" element={<NewPost tenantId={tenant} />} />

              {/* Super Admin (restricted server-side to SE3D / On-Screen tenants) */}
              <Route path="admin" element={<SuperAdminDashboard tenantId={tenant} />} />
            </Routes>
            
            <MessageThreadModal tenantId={tenant} />
          </div>
        </div>
      </div>
    </MessagesProvider>
  );
};

export default TenantRoutes;